import { atom } from 'jotai'

import { api } from '../lib/api'

export type MatchDecision = 'pending' | 'interested'

export type MatchProfile = {
  id: string
  name: string
  bio?: string
  birthday?: string
  gender?: string
  location?: string
  mainPhotoId?: string
  photoIds: string[]
  approvedPhotoIds: string[]
}

export type MatchHighlight = {
  key: string
  text: string
}

export type Match = {
  id: string
  score: number
  theirScore: number // How interested they are in you
  yourScore: number // How interested you are in them
  status: string
  yourDecision: MatchDecision
  awaitingThem: boolean
  /** Staff QA: match is against a simulated catalog persona. */
  isSimulated?: boolean
  profile: MatchProfile
  matchSummary?: string
  highlights?: MatchHighlight[]
  createdAt: string
}

type FormattedMatchResponse = {
  id: string
  score: number
  theirScore: number
  yourScore: number
  status: string
  yourDecision?: string
  awaitingThem?: boolean
  isSimulated?: boolean
  matchSummary?: string
  highlights?: MatchHighlight[]
  createdAt: string
}

type MatchWithProfileResponse = {
  match: FormattedMatchResponse
  profile: {
    id: string
    name: string
    bio?: string
    birthday?: string
    gender?: string
    location?: string
    mainPhotoId?: string
    photoIds?: string[]
    approvedPhotoIds?: string[]
  }
}

type ListMatchesResponse = {
  matches: MatchWithProfileResponse[]
}

type UnmatchResponse = {
  success: boolean
  matchId: string
  openRheya: boolean
  conversationSource: string
  alreadyComplete?: boolean
}

function mapMatchItem(item: MatchWithProfileResponse): Match {
  const decision = item.match.yourDecision === 'interested' ? 'interested' : 'pending'
  const match: Match = {
    id: item.match.id,
    score: item.match.score,
    theirScore: item.match.theirScore,
    yourScore: item.match.yourScore,
    status: item.match.status,
    yourDecision: decision,
    awaitingThem: Boolean(item.match.awaitingThem),
    createdAt: item.match.createdAt,
    profile: {
      id: item.profile.id,
      name: item.profile.name,
      photoIds: item.profile.photoIds ?? [],
      approvedPhotoIds: item.profile.approvedPhotoIds ?? [],
    },
  }
  if (item.match.isSimulated) match.isSimulated = true
  if (item.match.matchSummary) match.matchSummary = item.match.matchSummary
  if (item.match.highlights && item.match.highlights.length > 0)
    match.highlights = item.match.highlights
  if (item.profile.bio) match.profile.bio = item.profile.bio
  if (item.profile.birthday) match.profile.birthday = item.profile.birthday
  if (item.profile.gender) match.profile.gender = item.profile.gender
  if (item.profile.location) match.profile.location = item.profile.location
  if (item.profile.mainPhotoId) match.profile.mainPhotoId = item.profile.mainPhotoId
  return match
}

export const matchesAtom = atom<Match[]>([])

export const matchesIsLoadingAtom = atom(false)

export const matchesErrorAtom = atom<string | null>(null)

/** Client-held match id for unmatch resume same-tab; session field is authoritative after refresh. */
export const pendingUnmatchMatchIdAtom = atom<string | null>(null)

export const selectedMatchIdAtom = atom<string | null>(null)

export const selectedMatchAtom = atom((get) => {
  const matches = get(matchesAtom)
  const selectedId = get(selectedMatchIdAtom)
  if (!selectedId) return null
  return matches.find((m) => m.id === selectedId) ?? null
})

export const fetchMatchesAtom = atom(null, async function fetchMatches(_get, set) {
  set(matchesIsLoadingAtom, true)
  set(matchesErrorAtom, null)

  try {
    const response = await api.fetch('/matches')

    if (!response.ok) {
      set(matchesErrorAtom, 'Failed to fetch matches')
      set(matchesIsLoadingAtom, false)
      return
    }

    const data = (await response.json()) as ListMatchesResponse
    set(matchesAtom, data.matches.map(mapMatchItem))
    set(matchesIsLoadingAtom, false)
  } catch (_err) {
    set(matchesErrorAtom, 'Network error')
    set(matchesIsLoadingAtom, false)
  }
})

/** Express mutual interest. Applies server match body — never optimistic connected. */
export const expressInterestAtom = atom(
  null,
  async function expressInterest(get, set, matchId: string) {
    try {
      const response = await api.fetch(`/matches/${matchId}/interest`, {
        method: 'POST',
      })

      if (response.status === 409) {
        // Match closed by other party — treat as ended
        const matches = get(matchesAtom)
        set(
          matchesAtom,
          matches.filter((m) => m.id !== matchId),
        )
        if (get(selectedMatchIdAtom) === matchId) set(selectedMatchIdAtom, null)
        set(matchesErrorAtom, 'This introduction has ended.')
        return { success: false, ended: true as const }
      }

      if (!response.ok) {
        set(matchesErrorAtom, 'Failed to express interest')
        return { success: false }
      }

      const data = (await response.json()) as MatchWithProfileResponse
      const updated = mapMatchItem(data)
      const matches = get(matchesAtom)
      set(
        matchesAtom,
        matches.map((m) => (m.id === matchId ? updated : m)),
      )

      return { success: true, match: updated }
    } catch (_err) {
      set(matchesErrorAtom, 'Network error')
      return { success: false }
    }
  },
)

export type UnmatchResult =
  | { success: true; openRheya: boolean; matchId: string; alreadyComplete?: boolean }
  | { success: false; ended?: boolean }

/**
 * Unmatch: freeze/close on server, remove locally, optionally open Rheya.
 * 403/404 (other closed first / missing) → same ended UX as successful freeze for non-closer.
 */
export const unmatchAtom = atom(null, async function unmatch(get, set, matchId: string) {
  try {
    const response = await api.fetch(`/matches/${matchId}/unmatch`, {
      method: 'POST',
    })

    // Remove match from list for any terminal outcome the client should leave
    const removeLocal = () => {
      const matches = get(matchesAtom)
      set(
        matchesAtom,
        matches.filter((m) => m.id !== matchId),
      )
      if (get(selectedMatchIdAtom) === matchId) set(selectedMatchIdAtom, null)
    }

    if (response.status === 403 || response.status === 404) {
      removeLocal()
      set(matchesErrorAtom, 'This introduction has ended.')
      return { success: false, ended: true } satisfies UnmatchResult
    }

    if (!response.ok) {
      set(matchesErrorAtom, 'Failed to unmatch')
      return { success: false } satisfies UnmatchResult
    }

    const data = (await response.json()) as UnmatchResponse
    removeLocal()

    if (data.openRheya) {
      set(pendingUnmatchMatchIdAtom, data.matchId || matchId)
    }

    return {
      success: true,
      openRheya: Boolean(data.openRheya),
      matchId: data.matchId || matchId,
      ...(data.alreadyComplete ? { alreadyComplete: true } : {}),
    } satisfies UnmatchResult
  } catch (_err) {
    set(matchesErrorAtom, 'Network error')
    return { success: false } satisfies UnmatchResult
  }
})

export const selectMatchAtom = atom(null, function selectMatch(_get, set, matchId: string | null) {
  set(selectedMatchIdAtom, matchId)
})
